import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { createTheme, ThemeProvider } from "@mui/material";
import {
	ApiClientProctoringAssessmentConfigurationV2,
	ApiClientProctoringOptionV2,
	AssessmentInfo,
} from "@verity/api-client/types";
import {
	ProctoringAssessmentConfigurationV2,
	ProctoringOption,
	ProctoringOptionProfileV2,
} from "@verity/proctoring-option/types";
import { VerityLogger } from "@yujacorp/verity-logger";
import { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AssessmentUuids, api as defaultApi, VerityApi } from "../api/fetch";
import { CoreButton, CoreModal, coreToast, CoreToastContainer, ModalContext } from "../core";
import type { CoreFileItem } from "../core";
import { useMobileLayout } from "../hooks";
import ThemeWrapper, { themeToCssString } from "../theme";
import { getCombinedScrollbarThemeOverrides } from "../theme/scrollbar";
import { ExemptedStudent } from "../types/common";
import { EditQuizFooter, useEditQuizStyles } from "./components";
import { EditQuizToastContext } from "./context/EditQuizContext";
import { EditQuizPage, EditQuizPageProps, EditQuizRef, OnSaveOptionsResults } from "./editQuiz";
import { EditQuizSwitchComponent } from "./editQuizWrapper";
import styles from "./editQuizWrapper.module.css";
import EnableVeritySwitch from "./enableVeritySwitch";
import { useEditQuizButton } from "./hooks";
import useEditQuizWrapper, { UseEditQuizWrapperProps } from "./hooks/useEditQuizWrapper";
import { EditQuizAssessmentUUids, LiveSessionWithInvitations, PendingLiveSession } from "./live/liveHelper";
import { ConfigurationPageName } from "./proctoring-options/option-text/ProctoringOptionText";
import { checkIfVerityAssessment } from "./editQuizHelper";
import { uploadFilesToTempFolder } from "./ReferenceMaterialHelper";
import { QuickSetupHighlight, SETUP_GUIDE_ATTRIBUTES } from "../setup-guide";
import { getSetupTextByLMS } from "../setup-guide/QuickSetupText";
import { getExtendedLmsFromLms, SetupGuideContext } from "../setup-guide/quickSetupGuideHelper";

export type UnsavedAssessmentInfo = Partial<AssessmentInfo>;

export type TemporaryProctoringOptions = {
	assessmentInfo: UnsavedAssessmentInfo;
	profileId?: number;
	proctoringAssessmentConfiguration?:
		| Partial<Omit<ApiClientProctoringAssessmentConfigurationV2, "proctoringOptionProfile">>
		| undefined;
	pendingLiveSessions?: Record<string, PendingLiveSession> | undefined;
	assessmentUuids?: Omit<AssessmentUuids, "assessmentUuid"> | undefined;
	proctoringOption: Partial<ApiClientProctoringOptionV2> | undefined;
	exemptedStudents: Omit<ExemptedStudent, "name">[];
	referenceMaterialResourcesTempKey?: string;
};

export type InitialProctoringState = Partial<ProctoringOption> & {
	profileId?: number;
	existingLiveSessions?: Record<string, LiveSessionWithInvitations>;
	pendingLiveSessions?: Record<string, PendingLiveSession>;
	existingReferenceMaterialFiles?: CoreFileItem[];
};

export type EditQuizModalWrapperProps = {
	api?: VerityApi;
	quizName?: string;
	switchComponent?: EditQuizSwitchComponent;
	quizInfo: UnsavedAssessmentInfo;
	getUpdatedQuizInfo?: () => UnsavedAssessmentInfo | undefined;
	saveTemporaryProctoringAssessmentConfiguration: (options: TemporaryProctoringOptions) => Promise<void>;
	portalElement?: HTMLElement;
	cancelButtonHandler?: () => void;
	onSave?: (results?: OnSaveOptionsResults) => void;
	onModalOpenChange?: (open: boolean) => void;
	passwordFillTextOffset?: number;
	url?: string;
	logger?: VerityLogger;
	editButtonStyles?: React.CSSProperties;

	/* Dictionary of references to external HTML elements to use for events (Non-React) */
	externalRefs?: Record<string, HTMLElement>;
	initialPlatformId?: number;
	verityOrigin?: string;
	stylesheetUrl?: string;
} & Pick<UseEditQuizWrapperProps, "onInitialOptions" | "onEnabledChange" | "forceThirdParty"> &
	Pick<EditQuizPageProps, "quizType" | "checkThemeExists">;

export type EditQuizModalWrapperRef = {
	proctoringAssessmentConfiguration?: Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">;
	proctoringOption?: Partial<ProctoringOption>;
	outerSaveButtonHandler: (params: {
		additionalQuizInfo?: Partial<AssessmentInfo>;
		urlList?: string[];
	}) => Promise<void>;
	setEnabled: (enabled: boolean) => void;
	getEnabled: () => boolean;
	triggerOpenModal: (pageName: ConfigurationPageName) => void;
};

export const EditQuizModalWrapper = forwardRef(function EditQuizModalWrapper(
	{
		api = defaultApi,
		switchComponent,
		quizInfo,
		getUpdatedQuizInfo,
		quizName,
		saveTemporaryProctoringAssessmentConfiguration,
		portalElement,
		cancelButtonHandler,
		onInitialOptions,
		onEnabledChange,
		onModalOpenChange,
		forceThirdParty = false,
		url,
		editButtonStyles,
		logger,
		externalRefs,
		initialPlatformId,
		onSave,
		quizType = "lms",
		verityOrigin,
		stylesheetUrl,
		checkThemeExists,
	}: EditQuizModalWrapperProps,
	ref: ForwardedRef<EditQuizModalWrapperRef>,
) {
	const { saveProctoringAssessmentConfiguration } = api;

	const currFakeLiveSessionId = useRef(0);
	const assessmentUuids = useRef<EditQuizAssessmentUUids | undefined>();
	const [currentQuizInfo, setCurrentQuizInfo] = useState(quizInfo);

	useEffect(() => {
		setCurrentQuizInfo(quizInfo);
	}, [quizInfo]);

	const { action, state } = useEditQuizWrapper({
		quizType,
		api,
		quizInfo: currentQuizInfo,
		forceThirdParty,
		initialPlatformId,
		onInitialOptions,
		onEnabledChange,
		logger,
	});

	const {
		currentProfileId,
		pendingLiveSessions,
		pendingLiveSessionIds,
		existingLiveSessions,
		enforcedProfilesOnly,
		defaultProctoringOption,
		initialEnabledValue,
		initialProctoringAssessmentConfiguration,
		proctoringAssessmentConfiguration,
		initialProctoringOption,
		proctoringOption,
		existingReferenceMaterialFiles,
	} = state;
	const {
		discardSettings,
		handleEnabledChange,
		refreshSettings,
		setCurrentProfileId,
		setExistingLiveSessions,
		setPendingLiveSessionIds,
		setPendingLiveSessions,
		setProctoringAssessmentConfiguration,
		setProctoringOption,
		setInitialEnabledValue,
	} = action;

	const [iframeRoot, setIframeRoot] = useState<HTMLDivElement | null>(null);
	const [iframeHead, setIframeHead] = useState<HTMLHeadElement | null>(null);
	const [toastShowing, setToastShowing] = useState<boolean>(false);

	const [modalShowing, setModalShowing] = useState(false);
	const mergeQuizInfo = (
		previousQuizInfo: UnsavedAssessmentInfo,
		updatedQuizInfo?: UnsavedAssessmentInfo,
	): UnsavedAssessmentInfo => {
		if (!updatedQuizInfo) return previousQuizInfo;

		return {
			...previousQuizInfo,
			...updatedQuizInfo,
			quizInfo: updatedQuizInfo.quizInfo
				? {
						...(previousQuizInfo.quizInfo ?? {}),
						...updatedQuizInfo.quizInfo,
					}
				: previousQuizInfo.quizInfo,
		};
	};
	const getCurrentQuizInfoForSave = (additionalQuizInfo?: UnsavedAssessmentInfo) => {
		const updatedQuizInfo = getUpdatedQuizInfo?.();
		const mergedQuizInfo = mergeQuizInfo(mergeQuizInfo(currentQuizInfo, updatedQuizInfo), additionalQuizInfo);

		if (updatedQuizInfo || additionalQuizInfo) {
			setCurrentQuizInfo(mergedQuizInfo);
		}
		return mergedQuizInfo;
	};
	const refreshCurrentQuizInfo = () => {
		const updatedQuizInfo = getUpdatedQuizInfo?.();
		if (!updatedQuizInfo) return;

		setCurrentQuizInfo((previousQuizInfo) => mergeQuizInfo(previousQuizInfo, updatedQuizInfo));
	};
	const updateModalStatus = (showing: boolean) => {
		if (showing) {
			refreshCurrentQuizInfo();
		}
		populateThirdPartyURL(externalRefs);
		setModalShowing(showing);
		onModalOpenChange?.(showing);
		if (showing) {
			void logger?.assessment.edit.configurationModal.opened();
		} else {
			void logger?.assessment.edit.configurationModal.closed();
		}
	};
	const closeModal = () => updateModalStatus(false);
	const [isFinalPage, setIsFinalPage] = useState(false);
	const {
		backButton,
		closeButton,
		handleButtonChanges,
		nextButton,
		saveButton,
		saveProfileButton,
		buttonHandlers,
		getButtonRefHandler,
	} = useEditQuizButton();
	const editQuizPageRef = useRef<EditQuizRef>(null);

	//the exemptedstudentInfo that is used to upload
	const [uploadedExemptedStudentsMap, setUploadedExemptedStudentsMap] = useState<Map<number, ExemptedStudent>>(
		new Map(),
	);

	const [uploadedProfilesMap, setUploadedProfilesMap] = useState<Map<number, ProctoringOptionProfileV2>>(new Map());

	const outerSaveButtonHandler: EditQuizModalWrapperRef["outerSaveButtonHandler"] = async ({
		additionalQuizInfo,
		urlList,
	}) => {
		const latestQuizInfo = getCurrentQuizInfoForSave(additionalQuizInfo);

		void logger?.assessment.edit.saveButton.saveStarted({
			additionalQuizInfo,
			urlList,
		});

		if (!latestQuizInfo.quizId) {
			if (!proctoringAssessmentConfiguration?.enabled) return;
			const additionalQuizDetails = latestQuizInfo.quizInfo ?? {};
			try {
				const referenceMaterialResourcesTempKey = await uploadFilesToTempFolder(
					existingReferenceMaterialFiles,
					api.getReferenceMaterialUploadUrl,
				);
				await saveTemporaryProctoringAssessmentConfiguration({
					assessmentInfo: {
						...latestQuizInfo,
						quizInfo: {
							...additionalQuizDetails,
							name: typeof additionalQuizDetails.name === "string" ? additionalQuizDetails.name : quizName,
						},
					},
					profileId: currentProfileId,
					proctoringAssessmentConfiguration: proctoringAssessmentConfiguration,
					pendingLiveSessions: pendingLiveSessions,
					...(assessmentUuids.current && {
						assessmentUuids: {
							courseUuid: assessmentUuids.current.courseUuid,
							instituteUuid: assessmentUuids.current.instituteUuid,
						},
					}),
					proctoringOption: proctoringOption,
					exemptedStudents: Array.from(uploadedExemptedStudentsMap.values()).filter(
						(x) => !x.proctoringOptionProfileId || uploadedProfilesMap.has(x.proctoringOptionProfileId),
					),
					referenceMaterialResourcesTempKey,
				});

				void logger?.assessment.edit.saveButton.saveFinished(true);
			} catch (e) {
				void logger?.assessment.edit.saveButton.saveFinished(false);
				throw e;
			}
			return;
		}

		const { quizId } = latestQuizInfo;

		let newProctoringAssessmentConfiguration: Partial<ProctoringAssessmentConfigurationV2> | undefined;
		const newProctoringOption: Partial<ProctoringOption> = {};
		if (proctoringAssessmentConfiguration) {
			newProctoringAssessmentConfiguration = { enabled: proctoringAssessmentConfiguration.enabled };
			if (proctoringAssessmentConfiguration.password) {
				newProctoringAssessmentConfiguration.password = proctoringAssessmentConfiguration.password;
			}
			if (urlList) {
				newProctoringOption.tabWhiteList = urlList;
			}
			const thirdPartyUrlInputRef = externalRefs?.["thirdPartyUrlInputRef"]
				? (externalRefs.thirdPartyUrlInputRef as HTMLInputElement)
				: undefined;
			const finalThirdPartyUrl = thirdPartyUrlInputRef?.value || thirdPartyUrl;
			if (finalThirdPartyUrl && proctoringAssessmentConfiguration.thirdPartyAssessmentOption) {
				newProctoringAssessmentConfiguration.thirdPartyAssessmentOption = {
					...proctoringAssessmentConfiguration.thirdPartyAssessmentOption,
					thirdPartyUrl: finalThirdPartyUrl,
				};
			}
		}
		try {
			await saveProctoringAssessmentConfiguration({
				assessmentInfo: {
					...latestQuizInfo,
					quizId,
				},
				proctoringAssessmentConfiguration: newProctoringAssessmentConfiguration,
				proctoringOption:
					Object.keys(newProctoringOption).length > 0 ? { ...proctoringOption, ...newProctoringOption } : undefined,
			});
			void logger?.assessment.edit.saveButton.saveFinished(true);
		} catch (e) {
			void logger?.assessment.edit.saveButton.saveFinished(false);
			throw e;
		}
	};

	const setEnabled: EditQuizModalWrapperRef["setEnabled"] = async (enabled) => {
		const success = await handleEnabledChange(enabled);
		if (!enabled && success) {
			handleButtonChanges("save", { enabled: true });
		}
	};

	/* Retrieves the current status of the switch */
	const getEnabled: EditQuizModalWrapperRef["getEnabled"] = () => {
		return Boolean(proctoringAssessmentConfiguration && proctoringAssessmentConfiguration?.enabled);
	};

	useImperativeHandle(ref, () => ({
		proctoringAssessmentConfiguration,
		proctoringOption,
		outerSaveButtonHandler,
		setEnabled,
		getEnabled,
		triggerOpenModal,
	}));

	// Create iframe or sync
	useEffect(() => {
		refreshSettings();
		let iframe = document.getElementById(SETUP_GUIDE_ATTRIBUTES.ID.EDIT_QUIZ_IFRAME) as HTMLIFrameElement | null;
		if (iframe) {
			const doc = iframe.contentWindow?.document;
			if (doc) {
				setIframeHead(doc.head);
				setIframeRoot(doc.getElementById(SETUP_GUIDE_ATTRIBUTES.ID.EDIT_QUIZ_IFRAME_ROOT) as HTMLDivElement);
			}
			return;
		}

		iframe = document.createElement("iframe");

		Object.assign(iframe.style, {
			position: "fixed",
			top: "0",
			left: "0",
			width: "100vw",
			height: "100vh",
			border: "none",
			zIndex: SETUP_GUIDE_ATTRIBUTES.Z_INDEX.EDIT_QUIZ,
			pointerEvents: modalShowing ? "auto" : "none",
			display: modalShowing ? "block" : "none",
		});
		iframe.id = SETUP_GUIDE_ATTRIBUTES.ID.EDIT_QUIZ_IFRAME;

		document.body.appendChild(iframe);

		const doc = iframe.contentWindow?.document;
		if (doc) {
			// Inject the style bundle into the iframe
			if (stylesheetUrl) {
				const link = doc.createElement("link");
				link.rel = "stylesheet";
				link.href = stylesheetUrl;
				doc.head.appendChild(link);
			}

			// Need to define scrollbar styles cuz iframe
			const scrollbarStyles = getCombinedScrollbarThemeOverrides();
			const styleTag = doc.createElement("style");
			styleTag.id = SETUP_GUIDE_ATTRIBUTES.ID.SCROLLBAR_STYLE;
			styleTag.textContent = themeToCssString(scrollbarStyles);
			doc.head.appendChild(styleTag);

			doc.body.style.margin = "0";
			doc.body.style.height = "100%";
			const rootDiv = doc.createElement("div");
			rootDiv.id = SETUP_GUIDE_ATTRIBUTES.ID.EDIT_QUIZ_IFRAME_ROOT;
			doc.body.appendChild(rootDiv);

			setIframeHead(doc.head);
			setIframeRoot(rootDiv);
		}

		return () => {
			iframe.remove();
		};
	}, []);

	/* Trigger Opening of the modal to a specified page */
	const triggerOpenModal: EditQuizModalWrapperRef["triggerOpenModal"] = (pageName: ConfigurationPageName) => {
		refreshCurrentQuizInfo();
		editQuizPageRef.current?.goToPage(pageName);
		setModalShowing(true);
	};
	const [handledExternalRefs, setHandledExternalRefs] = useState<boolean>(false);
	const [thirdPartyUrl, setThirdPartyUrl] = useState<string>();
	/* Use Effect to handle external references */
	useEffect(() => {
		/* Do nothing if already handled */
		if (handledExternalRefs) {
			return;
		}

		/* Do nothing if there is no proctoring assessment configuration or reference or isVerityAssessment since we set the urls */
		if (!proctoringAssessmentConfiguration || !externalRefs || checkIfVerityAssessment(quizType)) {
			return;
		}

		/* If we have the reference to the external input field, update the state */
		if (proctoringAssessmentConfiguration.thirdPartyAssessmentOption && externalRefs["thirdPartyUrlInputRef"]) {
			const thirdPartyUrlInputRef = externalRefs["thirdPartyUrlInputRef"] as HTMLInputElement;
			let finalValidURL = "";
			if (!thirdPartyUrlInputRef.value) {
				const hiddenInput = document.getElementById(
					"assignment_external_tool_tag_attributes_custom_params",
				) as HTMLInputElement;
				const encodedValue = hiddenInput?.value;

				if (encodedValue) {
					try {
						const decoded = encodedValue.replace(/&quot;/g, '"').replace(/&amp;/g, "&");
						const parsed = JSON.parse(decoded);
						finalValidURL = parsed.redirectURL;
					} catch (err) {
						console.error("Error parsing custom_params:", err);
					}
				}
			} else {
				finalValidURL = thirdPartyUrlInputRef.value;
			}

			/* Update the state immediately */
			setProctoringAssessmentConfiguration({
				...proctoringAssessmentConfiguration,
				thirdPartyAssessmentOption: {
					...proctoringAssessmentConfiguration.thirdPartyAssessmentOption,
					thirdPartyUrl: finalValidURL,
				},
			});
			setThirdPartyUrl(finalValidURL);

			/* Handler for input update */
			const updateValue = () => {
				setProctoringAssessmentConfiguration((currentConfiguration) => ({
					...currentConfiguration,
					thirdPartyAssessmentOption: {
						...currentConfiguration?.thirdPartyAssessmentOption,
						thirdPartyUrl: thirdPartyUrlInputRef?.value,
					},
				}));
				setThirdPartyUrl(thirdPartyUrlInputRef.value);
			};

			/* Append listener and return */
			thirdPartyUrlInputRef.addEventListener("input", updateValue);
			setHandledExternalRefs(true);
		}
	}, [proctoringAssessmentConfiguration]);

	// Sync Iframe visibility with modal state
	useEffect(() => {
		const iframe = iframeRoot?.ownerDocument?.defaultView?.frameElement as HTMLIFrameElement;
		if (iframe) {
			const shouldBeVisible = modalShowing || toastShowing;

			iframe.style.display = shouldBeVisible ? "block" : "none";
			iframe.style.pointerEvents = modalShowing ? "auto" : "none";

			document.body.style.overflow = modalShowing ? "hidden" : "";
		}

		return () => {
			document.body.style.overflow = "";
		};
	}, [modalShowing, iframeRoot, toastShowing]);

	const isMobileLayout = useMobileLayout();
	const { getEditQuizModalProps } = useEditQuizStyles();

	let optionsPage;
	let editButton;

	const iframeCache = useMemo(() => {
		if (!iframeHead) return null;
		return createCache({
			key: "verity-modal-iframe",
			container: iframeHead,
		});
	}, [iframeHead]);

	const toastContainerId = `toast-${currentQuizInfo.quizId}`;
	if (proctoringAssessmentConfiguration?.enabled && iframeRoot && iframeHead) {
		const iframeTheme = createTheme({
			components: {
				MuiModal: { defaultProps: { container: iframeRoot } },
				MuiPopover: { defaultProps: { container: iframeRoot } },
				MuiPopper: { defaultProps: { container: iframeRoot } },
			},
		});
		optionsPage = createPortal(
			<CacheProvider value={iframeCache}>
				<ThemeProvider theme={iframeTheme}>
					<ThemeWrapper>
						<ModalContext.Provider value={{ modalContainer: { current: iframeRoot } }}>
							<SetupGuideContext.Provider
								value={{
									// Blackboard ultra does not use editQuizModalWrapper
									steps: quizInfo.lms
										? getSetupTextByLMS(
												getExtendedLmsFromLms(quizInfo.lms, false, quizInfo.quizInfo?.moodleVers as "3.X" | "4.X"),
												"create",
											)
										: [],
								}}
							>
								<CoreModal
									{...getEditQuizModalProps(isMobileLayout)}
									open={modalShowing}
									onClose={async () => {
										const wasClosed = await editQuizPageRef.current?.closeButtonHandler();

										if (wasClosed) {
											cancelButtonHandler?.();
											closeModal();
										}
									}}
									disableTopCloseButton={!closeButton.enabled}
									buttons={
										<EditQuizFooter
											slotProps={{
												back: {
													onClick: () => editQuizPageRef.current?.backButtonHandler(),
													disabled: !backButton.enabled,
													hide: backButton.hide,
												},
												confirm: {
													disabled: !saveButton.enabled || !closeButton.enabled,
													onClick: async () => {
														try {
															const isAccommodation = Boolean(editQuizPageRef.current?.getExemptionSectionInfo()?.view);

															// Do not update quizInfo if accommodation page, otherwise accommodation will not be saved since currentQuizInfo changes and causes refetching in useEditQuizInit
															await editQuizPageRef.current?.saveButtonHandler(
																undefined,
																isAccommodation ? undefined : getCurrentQuizInfoForSave(),
															);
														} catch (error) {
															console.error(error);
														}
													},
													hide: !isFinalPage || saveButton.hide,
													text: saveButton.text,
													ref: getButtonRefHandler("save"),
												},
												next: {
													disabled: !nextButton.enabled,
													onClick: () => editQuizPageRef.current?.nextButtonHandler(),
													hide: isFinalPage || nextButton.hide,
													ref: getButtonRefHandler("next"),
												},
												saveProfile: {
													disabled: !closeButton.enabled || !saveProfileButton.enabled,
													hide: enforcedProfilesOnly || !isFinalPage || saveProfileButton.hide,
													onClick: () => {
														editQuizPageRef.current?.saveAsProfileButtonHandler();
													},
												},
											}}
										/>
									}
								>
									<EditQuizPage
										url={thirdPartyUrl ?? url}
										ref={editQuizPageRef}
										api={api}
										quizInfo={currentQuizInfo}
										initialProctoringAssessmentConfiguration={initialProctoringAssessmentConfiguration}
										proctoringAssessmentConfiguration={proctoringAssessmentConfiguration}
										setProctoringAssessmentConfiguration={setProctoringAssessmentConfiguration}
										initialProctoringOption={initialProctoringOption}
										proctoringOption={proctoringOption}
										setProctoringOption={setProctoringOption}
										existingLiveSessions={existingLiveSessions}
										setExistingLiveSessions={setExistingLiveSessions}
										pendingLiveSessions={pendingLiveSessions}
										setPendingLiveSessions={setPendingLiveSessions}
										pendingLiveSessionIds={pendingLiveSessionIds}
										setPendingLiveSessionIds={setPendingLiveSessionIds}
										currFakeLiveSessionId={currFakeLiveSessionId}
										assessmentUuids={assessmentUuids}
										currentProfileId={currentProfileId}
										setCurrentProfileId={setCurrentProfileId}
										enforcedProfilesOnly={enforcedProfilesOnly}
										existingReferenceMaterialFiles={existingReferenceMaterialFiles}
										onClose={(discardedOptions?: InitialProctoringState) => {
											closeModal();
											discardSettings(discardedOptions?.existingReferenceMaterialFiles);
										}}
										setIsFinalPage={setIsFinalPage}
										onStartSaveOptions={(confirmedOptions?: InitialProctoringState) => {
											handleButtonChanges("close", { enabled: false });
											coreToast.dismiss();
											coreToast.loading("Applying your proctor settings...", {
												containerId: toastContainerId,
											});
											refreshSettings(confirmedOptions);
										}}
										onSaveOptions={(success, messages, _options, result) => {
											handleButtonChanges("close", { enabled: true });
											setToastShowing(true);
											if (success) {
												if (proctoringAssessmentConfiguration?.enabled) {
													setInitialEnabledValue(true);
												}
												coreToast.dismiss();
												coreToast.success("Your proctor settings have been successfully applied to the assessment.", {
													containerId: toastContainerId,
													size: "large",
												});
												onSave?.(result);
												cancelButtonHandler?.();
												closeModal();
											} else {
												coreToast.dismiss();
												coreToast.error(
													<>
														Failed to apply your proctor settings to the assessment{messages ? ": " : "."}
														{messages?.map((message) => (
															<div key={message}>{message}</div>
														))}
													</>,
													{
														containerId: toastContainerId,
													},
												);
											}
											setTimeout(() => {
												setToastShowing(false);
												coreToast.dismiss();
											}, 5000);
										}}
										uploadedExemptedStudentsMap={uploadedExemptedStudentsMap}
										setUploadedExemptedStudentsMap={setUploadedExemptedStudentsMap}
										uploadedProfilesMap={uploadedProfilesMap}
										setUploadedProfilesMap={setUploadedProfilesMap}
										logger={logger}
										defaultProctoringOption={defaultProctoringOption}
										forceStartOnFirstPage={!initialEnabledValue}
										quizType={quizType}
										entryPoint="lms-modal"
										verityOrigin={verityOrigin}
										checkThemeExists={checkThemeExists}
										{...buttonHandlers}
									/>
									{nextButton.highlightText && nextButton.element && (
										<QuickSetupHighlight
											targetRef={{ current: nextButton.element }}
											position="right"
											align="end"
											description={nextButton.highlightText}
										/>
									)}
									{saveButton.highlightText && saveButton.element && (
										<QuickSetupHighlight
											targetRef={{ current: saveButton.element }}
											position="right"
											align="end"
											description={saveButton.highlightText}
										/>
									)}
								</CoreModal>
							</SetupGuideContext.Provider>
						</ModalContext.Provider>
					</ThemeWrapper>
				</ThemeProvider>
			</CacheProvider>,
			iframeRoot,
		);

		const defaultEditButtonStyles: React.CSSProperties = {
			backgroundColor: "#fff",
		};
		const editButtonWrapperStyles = editButtonStyles
			? { ...defaultEditButtonStyles, ...editButtonStyles }
			: defaultEditButtonStyles;

		editButton = (
			<ThemeWrapper>
				<div style={editButtonWrapperStyles}>
					<CoreButton onClick={() => updateModalStatus(true)}>Configure Settings</CoreButton>
				</div>
			</ThemeWrapper>
		);

		if (portalElement) {
			editButton = createPortal(editButton, portalElement);
		} else {
			editButton = <div className={styles.root}>{editButton}</div>;
		}
	}

	const Switch = switchComponent ?? EnableVeritySwitch;

	function populateThirdPartyURL(externalRefs: Record<string, HTMLElement> | undefined) {
		try {
			if (quizType !== "lms") return;
			const thirdPartyUrlInputRef = externalRefs?.["thirdPartyUrlInputRef"] as HTMLInputElement;
			let finalValidURL = "";
			if (!thirdPartyUrlInputRef.value) {
				const hiddenInput = document.getElementById(
					"assignment_external_tool_tag_attributes_custom_params",
				) as HTMLInputElement;
				const encodedValue = hiddenInput?.value;

				if (encodedValue) {
					const decoded = encodedValue.replace(/&quot;/g, '"').replace(/&amp;/g, "&");
					const parsed = JSON.parse(decoded);
					finalValidURL = parsed.redirectURL;
				}
			} else {
				finalValidURL = thirdPartyUrlInputRef.value;
			}

			/* Update the state immediately */
			setProctoringAssessmentConfiguration((currentConfiguration) => ({
				...currentConfiguration,
				thirdPartyAssessmentOption: {
					...currentConfiguration?.thirdPartyAssessmentOption,
					thirdPartyUrl: finalValidURL,
				},
			}));
			setThirdPartyUrl(finalValidURL);
		} catch {
			return;
		}
	}

	const toastCache = useMemo(() => {
		if (!iframeHead) return null;
		return createCache({
			key: "verity-toast",
			container: iframeHead,
		});
	}, [iframeHead]);

	const toastPortal = iframeRoot
		? createPortal(
				<CacheProvider value={toastCache}>
					<ThemeWrapper>
						<CoreToastContainer enableMultiContainer containerId={toastContainerId} />
					</ThemeWrapper>
				</CacheProvider>,
				iframeRoot,
			)
		: null;

	return (
		<EditQuizToastContext.Provider value={toastContainerId}>
			<div id="root">
				{proctoringAssessmentConfiguration && proctoringOption && (
					<ThemeWrapper>
						<div style={{ position: "relative" }}>
							<Switch enabled={Boolean(proctoringAssessmentConfiguration.enabled)} setEnabled={setEnabled} />
						</div>
					</ThemeWrapper>
				)}
				{editButton}
				{optionsPage}
				{toastPortal}
			</div>
		</EditQuizToastContext.Provider>
	);
});
